import React, { useMemo, useState } from 'react';
import { Alert, Button, Empty, Popconfirm, Select, Spin, Table, Tag, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { openServerUrl, preopenWindow, userMessageForError } from '../../utils/apiError';
import { reportOpenFailure } from '../../utils/openLinkFeedback';
import { buildEnrollmentRows, canPublishTraining, attemptsLeft, certificateIssueText, formatDdMmYyyy, formatScore, sourceLabel } from '../../utils/trainingModel';

const { Text } = Typography;

/**
 * Logged training history — the A.6.3 audit evidence: who was enrolled in which course version,
 * when they completed, their server-scored result, how the completion arrived (in-app vs LMS
 * webhook) and the certificate. Advisor / Lead Advisor see every enrolment in the project;
 * the API returns learners only their own rows (training.controller.ts enrollmentsGet).
 */
const EnrollmentHistory = ({ projectId, role, courses, members }) => {
    const { request } = useApiRequest();
    const [courseFilter, setCourseFilter] = useState(null);
    const [certBusy, setCertBusy] = useState(null);

    const enrollments = useResource(
        async () => {
            const { data } = await request('GET', `/projects/${projectId}/training/enrollments`, {
                query: { courseId: courseFilter },
            });
            return data.enrollments || [];
        },
        [projectId, courseFilter, request],
    );

    const rows = useMemo(
        () => buildEnrollmentRows(enrollments.data, courses, members),
        [enrollments.data, courses, members],
    );

    const openCertificate = async (row) => {
        setCertBusy(row.id);
        const win = preopenWindow();
        try {
            const { data } = await request('GET', `/projects/${projectId}/training/enrollments/${row.id}/certificate`);
            if (!openServerUrl(data?.url, win)) reportOpenFailure(message, data?.url);
        } catch (err) {
            if (win && !win.closed) win.close();
            message.error(userMessageForError(err, 'Could not open the certificate.'));
        } finally {
            setCertBusy(null);
        }
    };

    const resetAttempts = async (row) => {
        setCertBusy(row.id);
        try {
            await request('POST', `/projects/${projectId}/training/enrollments/${row.id}/reset-attempts`);
            message.success('Attempts reset. The learner can try the quiz again.');
            enrollments.reload();
        } catch (err) {
            message.error(err.message || 'Could not reset attempts.');
        } finally {
            setCertBusy(null);
        }
    };

    const retryCertificate = async (row) => {
        setCertBusy(row.id);
        try {
            const { data } = await request('POST', `/projects/${projectId}/training/enrollments/${row.id}/certificate/retry`);
            if (data.certificateStatus === 'issued') message.success('Certificate issued.');
            else message.warning(data.certificateWarning || 'The certificate still could not be generated.');
            enrollments.reload();
        } catch (err) {
            message.error(err.message || 'Could not retry the certificate.');
        } finally {
            setCertBusy(null);
        }
    };

    const columns = [
        {
            title: 'Learner',
            key: 'learner',
            render: (_, r) => (
                <div>
                    <Text strong>{r.learnerName}</Text>
                    {r.learnerEmail && <div><Text type="secondary" style={{ fontSize: 12 }}>{r.learnerEmail}</Text></div>}
                </div>
            ),
        },
        { title: 'Course', key: 'course', render: (_, r) => `${r.courseTitle} (v${r.courseVersion})` },
        { title: 'Enrolment ID', dataIndex: 'id', key: 'id', render: (id) => <Text code copyable={{ text: id }}>{id.slice(0, 8)}</Text> },
        { title: 'Enrolled', dataIndex: 'enrolledAt', key: 'enrolledAt', width: 110, render: (d) => formatDdMmYyyy(d) },
        {
            title: 'Completed',
            key: 'completed',
            width: 130,
            render: (_, r) => (r.completed ? formatDdMmYyyy(r.completedAt) : r.attempted ? <Tag color="red">Not passed</Tag> : <Tag color="orange">Pending</Tag>),
        },
        { title: 'Score', key: 'score', width: 130, render: (_, r) => formatScore(r.score) },
        {
            title: 'Attempts',
            key: 'attemptCount',
            width: 190,
            render: (_, r) => {
                if (typeof r.attemptCount !== 'number') return '-';
                const left = attemptsLeft(r.maxAttempts, r.attemptCount);
                return (
                    <div>
                        <span>{r.attemptCount}{typeof r.maxAttempts === 'number' ? ` / ${r.maxAttempts}` : ''}</span>
                        {!r.completed && left === 0 && <Tag color="red" style={{ marginLeft: 6 }}>Locked out</Tag>}
                        {canPublishTraining(role) && !r.completed && r.attemptCount > 0 && (
                            <Popconfirm
                                title="Reset this learner's attempts?"
                                description="Clears their attempt count and last score so they can retry the quiz. The reset is recorded in the audit log."
                                okText="Reset"
                                onConfirm={() => resetAttempts(r)}
                            >
                                <Button size="small" style={{ marginLeft: 6 }} loading={certBusy === r.id}>Reset attempts</Button>
                            </Popconfirm>
                        )}
                    </div>
                );
            },
        },
        { title: 'Via', dataIndex: 'source', key: 'source', width: 80, render: (s) => sourceLabel(s) },
        {
            title: 'Certificate',
            key: 'certificate',
            width: 120,
            render: (_, r) =>
                r.hasCertificate ? (
                    <Button size="small" loading={certBusy === r.id} onClick={() => openCertificate(r)}>View</Button>
                ) : r.completed ? (
                    <div>
                        <Text type="danger" style={{ display: 'block', fontSize: 12 }}>{certificateIssueText(r.certificateIssue, true)}</Text>
                        <Button size="small" loading={certBusy === r.id} onClick={() => retryCertificate(r)}>Retry certificate</Button>
                    </div>
                ) : (
                    '-'
                ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <Text type="secondary">Attendance and outcomes, kept as audit evidence for ISO/IEC 27002:2022 A.6.3.</Text>
                <Select
                    allowClear
                    aria-label="Filter by course"
                    placeholder="All courses"
                    style={{ minWidth: 240 }}
                    value={courseFilter}
                    onChange={(v) => setCourseFilter(v || null)}
                    options={(courses || []).map((c) => ({ value: c.id, label: `${c.title} (v${c.version})` }))}
                />
            </div>
            {enrollments.status === 'loading' && !enrollments.data && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}
            {enrollments.status === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load training history"
                    description={enrollments.error?.message}
                    action={<Button size="small" onClick={enrollments.reload}>Retry</Button>}
                />
            )}
            {enrollments.status === 'ready' && rows.length === 0 && (
                <Empty description="No one has been enrolled yet. Enrol learners from a published course." />
            )}
            {rows.length > 0 && enrollments.status !== 'error' && (
                <Table rowKey={(r) => r.id} columns={columns} dataSource={rows} pagination={{ pageSize: 20, hideOnSinglePage: true }} loading={enrollments.status === 'loading'} />
            )}
        </div>
    );
};

export default EnrollmentHistory;
